import React, { useMemo, useRef, useState } from "react";
import { Badge } from "./ui/badge";

// Parse relative time strings like "T+0", "T+15min", "T+2h", "T+1h30min", "T+24h", "TBD" into minutes
function parseTimeToMinutes(timeStr) {
  if (!timeStr || timeStr === "TBD" || timeStr === "-") return null;
  const cleaned = timeStr.replace("T+", "").trim();
  if (cleaned === "0") return 0;

  let totalMin = 0;
  // Match days
  const dayMatch = cleaned.match(/(\d+)\s*(?:day|d)/i);
  if (dayMatch) totalMin += parseInt(dayMatch[1]) * 1440;
  // Match hours
  const hrMatch = cleaned.match(/(\d+)\s*h/i);
  if (hrMatch) totalMin += parseInt(hrMatch[1]) * 60;
  // Match minutes
  const minMatch = cleaned.match(/(\d+)\s*min/i);
  if (minMatch) totalMin += parseInt(minMatch[1]);

  // Handle bare numbers (assume hours if > 0 and no unit matched)
  if (!dayMatch && !hrMatch && !minMatch) {
    const num = parseFloat(cleaned);
    if (!isNaN(num)) totalMin = num * 60;
  }

  return totalMin;
}

function formatMinutes(min) {
  if (min === null || min === undefined) return "TBD";
  if (min < 60) return `${min}m`;
  if (min < 1440) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(min / 1440);
  const remaining = min % 1440;
  const h = Math.floor(remaining / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

// Color palette for task bars
const TASK_COLORS = [
  { bg: "#0ea5e9", light: "#e0f2fe", text: "#0c4a6e" }, // sky
  { bg: "#8b5cf6", light: "#ede9fe", text: "#4c1d95" }, // violet
  { bg: "#f59e0b", light: "#fef3c7", text: "#78350f" }, // amber
  { bg: "#10b981", light: "#d1fae5", text: "#064e3b" }, // emerald
  { bg: "#ef4444", light: "#fee2e2", text: "#7f1d1d" }, // red
  { bg: "#06b6d4", light: "#cffafe", text: "#164e63" }, // cyan
  { bg: "#ec4899", light: "#fce7f3", text: "#831843" }, // pink
  { bg: "#f97316", light: "#ffedd5", text: "#7c2d12" }, // orange
  { bg: "#6366f1", light: "#e0e7ff", text: "#312e81" }, // indigo
  { bg: "#14b8a6", light: "#ccfbf1", text: "#134e4a" }, // teal
  { bg: "#a855f7", light: "#f3e8ff", text: "#581c87" }, // purple
  { bg: "#84cc16", light: "#ecfccb", text: "#3f6212" }, // lime
];

export function RecoveryGanttChart({ tasks }) {
  const containerRef = useRef(null);
  const [hoveredTask, setHoveredTask] = useState(null);

  const chartData = useMemo(() => {
    if (!tasks || tasks.length === 0) return null;

    const parsed = tasks.map((t, i) => {
      const start = parseTimeToMinutes(t.start_time);
      const end = parseTimeToMinutes(t.completion_time);
      return {
        ...t,
        index: i,
        startMin: start ?? 0,
        endMin: end ?? (start != null ? start + 60 : 0),
        color: TASK_COLORS[i % TASK_COLORS.length],
      };
    });

    // Find max time for scale
    const maxTime = Math.max(...parsed.map(p => p.endMin), 60);

    // Build predecessor map for dependency lines
    const taskMap = {};
    parsed.forEach(p => { taskMap[p.task_id] = p; });

    // Identify critical path (longest chain from start to each task)
    const criticalSet = new Set();
    let longestEnd = 0;
    let longestTask = null;
    parsed.forEach(p => {
      if (p.endMin > longestEnd) {
        longestEnd = p.endMin;
        longestTask = p;
      }
    });

    // Trace back from longest task
    if (longestTask) {
      let current = longestTask;
      while (current) {
        criticalSet.add(current.task_id);
        current = current.predecessor_task_id ? taskMap[current.predecessor_task_id] : null;
      }
    }

    // Generate time markers
    const markers = [];
    let interval;
    if (maxTime <= 120) interval = 15;
    else if (maxTime <= 480) interval = 60;
    else if (maxTime <= 1440) interval = 120;
    else interval = 360;

    for (let t = 0; t <= maxTime; t += interval) {
      markers.push(t);
    }
    if (markers[markers.length - 1] < maxTime) {
      markers.push(maxTime);
    }

    return { parsed, maxTime, taskMap, criticalSet, markers, interval };
  }, [tasks]);

  if (!chartData) return null;

  const { parsed, maxTime, taskMap, criticalSet, markers } = chartData;
  const ROW_HEIGHT = 40;
  const LABEL_WIDTH = 180;
  const CHART_PADDING = 16;
  const chartWidth = 600;

  const getX = (min) => (min / maxTime) * chartWidth;

  return (
    <div className="space-y-3" data-testid="gantt-chart">
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: "#0ea5e9" }}></div>
          <span>Task duration</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm border-2" style={{ borderColor: "#ef4444", background: "transparent" }}></div>
          <span>Critical path</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="16" height="10"><line x1="0" y1="5" x2="16" y2="5" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3,2"/></svg>
          <span>Dependency</span>
        </div>
      </div>

      {/* Chart */}
      <div ref={containerRef} className="border rounded-lg overflow-x-auto bg-white" style={{ maxWidth: "100%" }}>
        <div style={{ minWidth: LABEL_WIDTH + chartWidth + CHART_PADDING * 2 + 20 }}>
          {/* Time axis header */}
          <div className="flex border-b bg-slate-50/80" style={{ height: 32 }}>
            <div style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }} className="px-3 flex items-center text-xs font-semibold text-slate-600 border-r">
              Task
            </div>
            <div className="relative flex-1" style={{ paddingLeft: CHART_PADDING, paddingRight: CHART_PADDING }}>
              {markers.map((m, i) => (
                <div
                  key={i}
                  className="absolute text-[10px] text-slate-400 font-mono"
                  style={{ left: CHART_PADDING + getX(m), top: 8, transform: "translateX(-50%)" }}
                >
                  {formatMinutes(m)}
                </div>
              ))}
            </div>
          </div>

          {/* Task rows */}
          {parsed.map((task, idx) => {
            const barStart = getX(task.startMin);
            const barWidth = Math.max(getX(task.endMin) - barStart, 4);
            const isCritical = criticalSet.has(task.task_id);
            const isHovered = hoveredTask === task.task_id;
            const predecessor = task.predecessor_task_id ? taskMap[task.predecessor_task_id] : null;

            return (
              <div
                key={task.task_id}
                className={`flex border-b last:border-b-0 transition-colors ${isHovered ? "bg-slate-50" : ""}`}
                style={{ height: ROW_HEIGHT }}
                onMouseEnter={() => setHoveredTask(task.task_id)}
                onMouseLeave={() => setHoveredTask(null)}
              >
                {/* Label */}
                <div
                  style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }}
                  className="px-3 flex items-center gap-2 border-r overflow-hidden"
                >
                  <span
                    className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ background: task.color.light, color: task.color.text }}
                  >
                    {task.task_id}
                  </span>
                  <span className="text-xs text-slate-700 truncate" title={task.actual_task}>
                    {task.actual_task.length > 22 ? task.actual_task.slice(0, 22) + "..." : task.actual_task}
                  </span>
                </div>

                {/* Bar area */}
                <div className="relative flex-1" style={{ paddingLeft: CHART_PADDING, paddingRight: CHART_PADDING }}>
                  {/* Grid lines */}
                  {markers.map((m, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0"
                      style={{ left: CHART_PADDING + getX(m), width: 1, background: "#f1f5f9" }}
                    />
                  ))}

                  {/* Dependency line */}
                  {predecessor && (
                    <svg
                      className="absolute top-0 left-0 pointer-events-none"
                      style={{ width: "100%", height: ROW_HEIGHT, overflow: "visible" }}
                    >
                      <line
                        x1={CHART_PADDING + getX(predecessor.endMin)}
                        y1={-ROW_HEIGHT / 2}
                        x2={CHART_PADDING + barStart}
                        y2={ROW_HEIGHT / 2}
                        stroke="#94a3b8"
                        strokeWidth="1.5"
                        strokeDasharray="4,3"
                        opacity="0.6"
                      />
                      {/* Arrow head */}
                      <polygon
                        points={`${CHART_PADDING + barStart},${ROW_HEIGHT / 2} ${CHART_PADDING + barStart - 4},${ROW_HEIGHT / 2 - 3} ${CHART_PADDING + barStart - 4},${ROW_HEIGHT / 2 + 3}`}
                        fill="#94a3b8"
                        opacity="0.6"
                      />
                    </svg>
                  )}

                  {/* Task bar */}
                  <div
                    className="absolute rounded-md transition-all cursor-pointer"
                    style={{
                      left: CHART_PADDING + barStart,
                      width: barWidth,
                      top: (ROW_HEIGHT - 22) / 2,
                      height: 22,
                      background: task.color.bg,
                      opacity: isHovered ? 1 : 0.85,
                      boxShadow: isHovered ? `0 2px 8px ${task.color.bg}40` : "none",
                      outline: isCritical ? "2px solid #ef4444" : "none",
                      outlineOffset: "1px",
                      zIndex: 2,
                    }}
                    title={`${task.task_id}: ${task.actual_task}\nOwner: ${task.task_owner}\nDuration: ${task.estimated_duration}\n${task.start_time} → ${task.completion_time}`}
                  >
                    {barWidth > 50 && (
                      <span className="text-[9px] text-white font-medium px-1.5 leading-[22px] truncate block">
                        {task.estimated_duration}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover tooltip card */}
      {hoveredTask && (() => {
        const task = parsed.find(t => t.task_id === hoveredTask);
        if (!task) return null;
        return (
          <div className="flex items-start gap-3 px-3 py-2 bg-slate-50 rounded-lg border text-xs animate-in fade-in duration-150">
            <Badge
              className="text-[10px] font-mono shrink-0"
              style={{ background: task.color.light, color: task.color.text }}
            >
              {task.task_id}
            </Badge>
            <div className="min-w-0">
              <p className="font-medium text-slate-800">{task.actual_task}</p>
              <div className="flex gap-4 mt-1 text-slate-500">
                <span>Owner: <strong className="text-slate-700">{task.task_owner}</strong></span>
                <span>{task.start_time} → {task.completion_time}</span>
                <span>Duration: {task.estimated_duration}</span>
                {task.predecessor_task_id && <span>Depends on: <code className="text-xs">{task.predecessor_task_id}</code></span>}
              </div>
              {task.notes && <p className="text-slate-400 mt-0.5">{task.notes}</p>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
